import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter} from "@/components/ui/dialog.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import {
    Form,
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage
} from "@/components/ui/form.tsx";
import {useForm} from "react-hook-form";
import {usePage} from "@inertiajs/react";
import {PatientFormPayload, PatientSchema} from "@/lib/types/forms.ts";
import {zodResolver} from "@hookform/resolvers/zod";
import {Doctor, Patient} from "@/lib/types/models.ts";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {useEffect} from "react";

interface CreatePatientDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onCreate: (data: PatientFormPayload) => Promise<Patient>;
    onCreated?: (patient: Patient) => void;
    doctors: Doctor[];
    orthodontists: { id: number; name: string }[];
    defaultDoctorId?: string;
}

export default function CreatePatientDialog({
                                                isOpen,
                                                onOpenChange,
                                                onCreate,
                                                onCreated,
                                                doctors,
                                                orthodontists,
                                                defaultDoctorId,
                                            }: CreatePatientDialogProps) {
    const {translations} = usePage().props as any;
    const t = translations.create_patient_dialog;

    const form = useForm<any>({
        resolver: zodResolver(PatientSchema),
        defaultValues: {
            name: "",
            phone_number: "",
            address: "",
            city: "",
            year_of_birth: undefined,
            call_before_appointment: false,
            doctor_id: defaultDoctorId || "",
            orthodontics: false,
            orthodontist_id: undefined,
            note: "",
            xray: "",
        },
    });

    useEffect(() => {
        if (!isOpen) return;
        form.reset({
            name: "",
            phone_number: "",
            address: "",
            city: "",
            year_of_birth: undefined,
            call_before_appointment: false,
            doctor_id: defaultDoctorId || "",
            orthodontics: false,
            orthodontist_id: undefined,
            note: "",
            xray: "",
        });
    }, [isOpen, form]);

    const handleSubmit = async (data: any) => {
        const payload: PatientFormPayload = {
            address: data.address ?? undefined,
            call_before_appointment: !!data.call_before_appointment,
            city: data.city ?? undefined,
            name: data.name,
            note: data.note ?? undefined,
            orthodontics: !!data.orthodontics,
            phone_number: data.phone_number ?? undefined,
            xray: data.xray ?? undefined,
            year_of_birth: data.year_of_birth ?? undefined,
            doctor_id: data.doctor_id,
            orthodontist_id: data.orthodontist_id ?? null,
        } as PatientFormPayload;
        const createdPatient = await onCreate(payload);
        onCreated?.(createdPatient);
        onOpenChange(false);
    };

    const orthodonticsEnabled = form.watch("orthodontics");

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-auto">
                <DialogHeader>
                    <DialogTitle>{t.title}</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">

                        <FormField control={form.control} name="name" render={({field, fieldState}) => (
                            <FormItem>
                                <FormLabel>{t.labels.name}</FormLabel>
                                <FormControl><Input {...field} placeholder={t.placeholders.name}/></FormControl>
                                <FormMessage>{fieldState?.error?.message}</FormMessage>
                            </FormItem>
                        )}/>

                        <FormField control={form.control} name="address" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.labels.address}</FormLabel>
                                <FormControl><Input {...field} placeholder={t.placeholders.address}/></FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <FormField control={form.control} name="city" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.labels.city}</FormLabel>
                                <FormControl><Input {...field} placeholder={t.placeholders.city}/></FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <FormField control={form.control} name="phone_number" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.labels.phone_number}</FormLabel>
                                <FormControl><Input {...field} placeholder={t.placeholders.phone_number}/></FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <FormField control={form.control} name="year_of_birth" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.labels.year_of_birth}</FormLabel>
                                <FormControl>
                                    <Input
                                        {...field}
                                        placeholder={t.placeholders.year_of_birth}
                                        type="number"
                                        value={field.value ?? ""}
                                        onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                                    />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <div className="flex flex-row items-center space-x-6">
                            <FormField control={form.control} name="orthodontics" render={({field}) => (
                                <FormItem className="flex flex-row items-center space-x-2">
                                    <FormControl>
                                        <Checkbox checked={field.value}
                                                  onCheckedChange={(checked) => field.onChange(checked)}/>
                                    </FormControl>
                                    <FormLabel>{t.labels.orthodontics}</FormLabel>
                                </FormItem>
                            )}/>

                            <FormField control={form.control} name="call_before_appointment" render={({field}) => (
                                <FormItem className="flex flex-row items-center space-x-2">
                                    <FormControl>
                                        <Checkbox checked={field.value}
                                                  onCheckedChange={(checked) => field.onChange(checked)}/>
                                    </FormControl>
                                    <FormLabel>{t.labels.call_before_appointment}</FormLabel>
                                </FormItem>
                            )}/>
                        </div>

                        <FormField control={form.control} name="doctor_id" render={({field, fieldState}) => (
                            <FormItem>
                                <FormLabel>{t.labels.doctor}</FormLabel>
                                <FormControl>
                                    <Select
                                        value={field.value || ""}
                                        onValueChange={(val) => field.onChange(val)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={t.placeholders.select_doctor}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {doctors.map((doc) => (
                                                <SelectItem key={doc.id} value={doc.id}>{doc.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage>{fieldState?.error?.message}</FormMessage>
                            </FormItem>
                        )}/>

                        {orthodonticsEnabled && (
                            <FormField control={form.control} name="orthodontist_id" render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t.labels.orthodontist}</FormLabel>
                                    <FormControl>
                                        <Select
                                            value={field.value?.toString() || ""}
                                            onValueChange={(val) => field.onChange(Number(val))}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder={t.placeholders.select_orthodontist}/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {orthodontists.map((doc) => (
                                                    <SelectItem key={doc.id}
                                                                value={doc.id.toString()}>{doc.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}/>
                        )}

                        <FormField control={form.control} name="note" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.labels.note}</FormLabel>
                                <FormControl>
                  <textarea
                      {...field}
                      rows={4}
                      className="w-full rounded-md border px-3 py-2 text-sm"
                      placeholder={t.placeholders.note}
                  />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <FormField control={form.control} name="xray" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.labels.xray}</FormLabel>
                                <FormControl>
                  <textarea
                      {...field}
                      rows={2}
                      className="w-full rounded-md border px-3 py-2 text-sm"
                      placeholder={t.placeholders.xray}
                  />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <DialogFooter className="flex justify-end gap-2">
                            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                                {t.buttons.cancel}
                            </Button>
                            <Button type="submit">{t.buttons.create}</Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}